import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import T, { bool, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { NotifyTypo } from 'web-components';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { getSensorReducerState } from 'redux/rootSelectors';
import { isError, isLoading, isSuccess } from 'helpers/utils';
import { deleteSensor, loadSensor } from '../../../../../redux/sensor/actions';
import { StyledHandlers } from '../../elements';
import { StyledContent, StyledNewDialog } from '../elements';
import { deleteSensorIrisV3 } from '../../../../../redux/machines_v2/actions';
import { isIrisV3Variant } from '../../../../../attrs/plcConfigValues';

const labelAmend = 'machines.machine_details.delete_modal';

const successToastProps = {
  variant: 'success',
  autoHideDuration: 3000
};

const errorToastProps = {
  variant: 'error',
  autoHideDuration: 3000
};

const DeleteSensor = ({ open, handleClose, machineId, sensorType, plcType, selectedItemId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { deletingSensorStatus } = useSelector(getSensorReducerState);
  const [successDeletingSensor, setSuccessDeletingSensor] = useState(false);
  const [errorDeletingSensor, setErrorDeletingSensor] = useState(false);

  useEffect(() => {
    if (open) {
      if (isSuccess(deletingSensorStatus.status)) {
        setSuccessDeletingSensor(true);
      }
      if (isError(deletingSensorStatus.status)) {
        setErrorDeletingSensor(true);
      }
    }
  }, [deletingSensorStatus.status, open]);

  useEffect(() => {
    if (successDeletingSensor && open) {
      dispatch(loadSensor(machineId));
      enqueueSnackbar(t('machines.form.sensor.message.deleting_success'), successToastProps);
      setSuccessDeletingSensor(false);
      handleClose();
    }
  }, [dispatch, enqueueSnackbar, handleClose, machineId, open, successDeletingSensor, t]);

  useEffect(() => {
    if (errorDeletingSensor) {
      enqueueSnackbar(t('machines.form.sensor.message.deleting_error'), errorToastProps);
      setErrorDeletingSensor(false);
    }
  }, [enqueueSnackbar, errorDeletingSensor, t]);

  const handleDeleteSensor = async () => {
    if (isIrisV3Variant(plcType.plc_type)) {
      dispatch(deleteSensorIrisV3(machineId, selectedItemId));
      handleClose();
    } else {
      dispatch(deleteSensor(machineId, sensorType));
    }
  };

  function isDeleting() {
    return isLoading(deletingSensorStatus.status);
  }

  const actionBar = (
    <StyledHandlers>
      <Button color="secondary" onClick={() => handleClose()} disabled={isDeleting()}>
        {t(`${labelAmend}.cancel`)}
      </Button>
      <Button
        variant="contained"
        data-testid="license-modal-submit"
        style={{ padding: '1rem 2.5rem' }}
        color="error"
        onClick={handleDeleteSensor}
        disabled={isDeleting()}
      >
        {t(`${labelAmend}.action`)}
        {isDeleting() && <CircularProgress sx={{ marginLeft: 1, fontSize: '1.125rem' }} size={18} />}
      </Button>
    </StyledHandlers>
  );

  return (
    <StyledNewDialog
      open={open}
      handleClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      title={t(`${labelAmend}.static_title`)}
      subTitle={t(`${labelAmend}.static_subtitle`)}
      displayActionBar="true"
      actionBar={actionBar}
      maxWidth="680px"
      content={
        <StyledContent>
          <NotifyTypo.Heading2>{t(`${labelAmend}.static_text`)}</NotifyTypo.Heading2>
        </StyledContent>
      }
    />
  );
};

DeleteSensor.propTypes = {
  open: bool.isRequired,
  handleClose: func.isRequired,
  machineId: T.string.isRequired,
  sensorType: T.string.isRequired,
  plcType: T.shape({
    plc_type: T.string.isRequired,
    autoconfig_enabled: T.bool
  }).isRequired,
  selectedItemId: T.number
};

DeleteSensor.defaultProps = {
  selectedItemId: null
};

export default DeleteSensor;
